import { useState, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Search, MoreHorizontal, Eye, Trash2, Settings } from "lucide-react";
import { useTran } from "@/hooks";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { InfoDot } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { ConfirmDialog, PaginationControls } from "@/components/common";
import { DataTable } from "@/components/common/DataTable";
import ViewBadge from "@/components/common/ViewBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import ReportingBoundaryDetailDialog from "./ReportingBoundaryDetailDialog";
import { useNavigate } from "react-router-dom";
import { deleteReportingYear } from "../operations/reporting-boundary.ops";
import { MONTHS } from "@/features/esg/utils/period-constants";
import { Can } from "@/hooks/useCan/Can";
import ViewOrgs from "@/components/ui/ViewOrgs";

const PAGE_SIZE = 10;

const TABLE_HEADER_CLASS = "";
//const TABLE_HEADER_CLASS = "bg-slate-100 text-slate-700";

const formatMonth = (month) => MONTHS[month - 1] || "-";

const formatPeriod = (row) => {
  if (!row?.startYear || !row?.startMonth) return "-";
  if (!row?.endYear || !row?.endMonth) return "-";

  return `${formatMonth(row.startMonth)} ${row.startYear} - ${formatMonth(row.endMonth)} ${row.endYear}`;
};

const getMetricsCount = (row = {}) => {
  const value = row.totalDisclosures ?? row.metricsCount ?? row.metrics;
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

const FrameworkChips = ({
  codes = [],
  limit = 2,
  title = "Frameworks",
  emptyMessage = "No frameworks",
  getFrameworkName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const frameworks = Array.isArray(codes)
    ? codes
        .map((code) => ({
          code,
          label: getFrameworkName(code),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];
  const visibleFrameworks = frameworks.slice(0, limit);
  const hiddenCount = frameworks.length - visibleFrameworks.length;

  if (frameworks.length === 0) {
    return (
      <span className="text-xs italic text-muted-foreground">
        {emptyMessage}
      </span>
    );
  }

  const Chip = ({ framework, compact = false }) => (
    <span
      title={framework.label}
      className={`inline-flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs font-medium text-foreground shadow-sm ${
        compact ? "max-w-full" : "max-w-[220px]"
      }`}
      style={{
        borderColor: "#05966940",
        backgroundColor: "#0596690f",
      }}
    >
      {/* <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: framework.color }}
      /> */}
      <span className="truncate text-emerald-700">{framework.label}</span>
    </span>
  );

  return (
    <div className="flex max-w-[360px] flex-wrap items-center gap-1.5">
      {visibleFrameworks.map((framework) => (
        <Chip key={framework.code} framework={framework} />
      ))}

      {hiddenCount > 0 ? (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex h-7 items-center rounded-md border border-border bg-muted/50 px-2 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              +{hiddenCount}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={12}
            className="w-[min(24rem,calc(100vw-1rem))] rounded-md border-border bg-card p-0 text-foreground shadow-lg"
          >
            <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-2">
              <span className="text-xs font-semibold">{title}</span>
              <span className="text-[11px] text-muted-foreground">
                {frameworks.length} total
              </span>
            </div>
            <div className="max-h-56 overflow-y-auto p-3">
              <div className="flex flex-col gap-2">
                {frameworks.map((framework) => (
                  <Chip key={framework.code} framework={framework} compact />
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
};

const ReportingBoundaryTable = observer(
  ({
    data = [],
    loading = false,
    organizationTree = [],
    onDeleted,
    availableFrameworks = [],
    permissions = [],
    isUserLevel = true,
  }) => {
    const { t } = useTran();
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailRow, setDetailRow] = useState(null);
    const [deleteRow, setDeleteRow] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const getFrameworkName = useCallback(
      (code) => {
        if (
          !Array.isArray(availableFrameworks) ||
          availableFrameworks.length === 0
        ) {
          return code;
        }

        const framework = availableFrameworks.find(
          (item) => item.code === code,
        );

        return framework?.name || code;
      },
      [availableFrameworks],
    );

    const navigate = useNavigate();

    const handleEdit = (reportingYear) => {
      navigate(`/esg/reporting-boundary/edit/${reportingYear._id}`);
    };

    // Search
    const [searchQuery, setSearchQuery] = useState("");

    // Status filter
    const [statusFilter] = useState("all");

    // Sort
    const [sort, setSort] = useState({ key: "startYear", direction: "desc" });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    // Handle sort
    const handleSort = (key) => {
      setSort((prev) => {
        if (prev.key === key) {
          return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
        }
        return { key, direction: "asc" };
      });
      setCurrentPage(1);
    };

    // Process data: search → filter → sort
    const processedData = useMemo(() => {
      let filtered = [...data];

      // Search
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (row) =>
            String(row.startYear).includes(query) ||
            row.type?.toLowerCase().includes(query) ||
            row.frameworkCodes?.some((code) =>
              getFrameworkName(code).toLowerCase().includes(query),
            ) ||
            row.frameworks?.some((fw) =>
              fw.name.toLowerCase().includes(query),
            ) ||
            row.organizations?.some((org) =>
              org.name.toLowerCase().includes(query),
            ) ||
            row.status?.toLowerCase().includes(query),
        );
      }

      // Status filter
      if (statusFilter !== "all") {
        filtered = filtered.filter((row) => row.status === statusFilter);
      }

      // Sort
      filtered.sort((a, b) => {
        let aVal, bVal;
        switch (sort.key) {
          case "startYear":
            aVal = a.startYear || 0;
            bVal = b.startYear || 0;
            return sort.direction === "asc" ? aVal - bVal : bVal - aVal;
          case "type":
            aVal = a.type || "";
            bVal = b.type || "";
            break;
          case "status":
            aVal = a.status || "";
            bVal = b.status || "";
            break;
          case "organizations":
            aVal = a.organizations?.length || 0;
            bVal = b.organizations?.length || 0;
            return sort.direction === "asc" ? aVal - bVal : bVal - aVal;
          case "metrics":
            aVal = getMetricsCount(a);
            bVal = getMetricsCount(b);
            return sort.direction === "asc" ? aVal - bVal : bVal - aVal;
          default:
            return 0;
        }
        const comparison = String(aVal).localeCompare(String(bVal));
        return sort.direction === "asc" ? comparison : -comparison;
      });

      return filtered;
    }, [data, getFrameworkName, searchQuery, statusFilter, sort]);

    // Pagination calculations
    const totalItems = processedData.length;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);
    const paginatedData = processedData.slice(startIndex, endIndex);

    const handleDeleteConfirm = async () => {
      const id = deleteRow?._id || deleteRow?.id;
      setDeleting(true);
      const deleted = await deleteReportingYear(id);
      setDeleting(false);

      if (!deleted) return;

      setDeleteRow(null);
      await onDeleted?.();
    };

    // Column definitions
    const columns = [
      {
        key: "startYear",
        header: t("Reporting Year"),
        sortable: true,
        showSortIndicators: true,
        width: "130px",
        headerClassName: TABLE_HEADER_CLASS,
        render: (row) => (
          <div className="flex items-center gap-2">
            <span className="font-semibold">{row.startYear}</span>
          </div>
        ),
      },
      {
        key: "period",
        header: t("Period"),
        sortable: false,
        headerClassName: TABLE_HEADER_CLASS,
        render: (row) => (
          <span className="text-sm text-muted-foreground">
            {formatPeriod(row, row.type)}
          </span>
        ),
      },
      {
        key: "frameworks",
        header: t("Frameworks"),
        sortable: false,
        headerClassName: TABLE_HEADER_CLASS,
        render: (row) => (
          <FrameworkChips
            getFrameworkName={getFrameworkName}
            codes={row.frameworkCodes}
            limit={2}
            title={t("Frameworks")}
            emptyMessage={t("No frameworks")}
          />
        ),
      },
      {
        key: "organizations",
        header: t("Organizations"),
        sortable: true,
        showSortIndicators: true,
        headerClassName: TABLE_HEADER_CLASS,
        render: (row) => (
          <ViewOrgs orgs={row.organizations} tree={organizationTree} />
        ),
      },
      // {
      //   key: "metrics",
      //   header: t("Metrics"),
      //   sortable: true,
      //   showSortIndicators: true,
      //   width: "110px",
      //   align: "center",
      //   headerClassName: TABLE_HEADER_CLASS,
      //   render: (row) => {
      //     const totalMetrics = getMetricsCount(row);
      //     const assignedMetrics = row?.completionSummary?.assigned ?? 0;

      //     return (
      //       <div className="flex justify-center">
      //         <div className="inline-flex min-w-[104px] flex-col overflow-hidden rounded-md border border-slate-200 bg-white text-left shadow-sm">
      //           <div className="flex items-center justify-between gap-2 bg-slate-50 px-2.5 py-1">
      //             <span className="text-[11px] font-medium text-slate-500">
      //               {t("Total")}
      //             </span>
      //             <Badge className="h-5 min-w-6 justify-center border-slate-200 bg-white px-1.5 text-xs font-semibold text-slate-800 shadow-none">
      //               {totalMetrics}
      //             </Badge>
      //           </div>
      //           <div className="flex items-center justify-between gap-2 px-2.5 py-1">
      //             <span className="text-[11px] font-medium text-emerald-700">
      //               {t("Assigned")}
      //             </span>
      //             <span className="inline-flex items-center gap-1">
      //               <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">
      //                 {assignedMetrics}
      //               </span>
      //               {/* <InfoDot
      //                 tip={t("Assigned metrics: {{assigned}}", {
      //                   assigned: assignedMetrics,
      //                 })}
      //               /> */}
      //             </span>
      //           </div>
      //         </div>
      //       </div>
      //     );
      //   },
      // },
      // {
      //   key: "completion",
      //   header: t("Completion"),
      //   sortable: false,
      //   width: "180px",
      //   headerClassName: TABLE_HEADER_CLASS,
      //   render: (row) => {
      //     const progress = getCompletionProgress(row);

      //     return (
      //       <div className="flex min-w-[150px] flex-col gap-2">
      //         <HorizontalProgress
      //           label={t("Target Setup")}
      //           value={progress.targetSetup}
      //           barClassName="bg-blue-600"
      //         />
      //         <HorizontalProgress
      //           label={t("Data Entry")}
      //           value={progress.dataEntry}
      //           barClassName="bg-emerald-600"
      //         />
      //       </div>
      //     );
      //   },
      // },
      // {
      //   key: "status",
      //   header: t("Status"),
      //   sortable: true,
      //   showSortIndicators: true,
      //   width: "140px",
      //   headerClassName: TABLE_HEADER_CLASS,
      //   render: (row) => {
      //     const config = STATUS_CONFIG[row.status];
      //     return config ? (
      //       <Badge className={`${config.className} border gap-1.5`}>
      //         <span
      //           className={`h-1.5 w-1.5 rounded-full ${config.dotClassName}`}
      //         />
      //         {t(config.label)}
      //       </Badge>
      //     ) : (
      //       <span className="text-xs text-muted-foreground">-</span>
      //     );
      //   },
      // },
      {
        key: "actions",
        header: t("Actions"),
        width: "70px",
        align: "center",
        headerClassName: TABLE_HEADER_CLASS,
        render: (row) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setDetailRow(row);
                  setDetailOpen(true);
                }}
              >
                <Eye className="h-4 w-4 text-emerald-600" />
                {t("View Details")}
              </DropdownMenuItem>
              <Can
                resource="reporting_boundary"
                action="update"
                enabled={!isUserLevel}
                userPermissions={permissions}
              >
                <DropdownMenuItem onClick={() => handleEdit(row)}>
                  <Settings className="h-4 w-4 text-blue-600" />
                  {t("Edit")}
                </DropdownMenuItem>
              </Can>
              <Can
                resource="reporting_boundary"
                action="delete"
                enabled={!isUserLevel}
                userPermissions={permissions}
              >
                {!row?.isAssignedMetrics ? (
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => setDeleteRow(row)}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                    {t("Delete")}
                  </DropdownMenuItem>
                ) : null}{" "}
              </Can>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ];

    return (
      <div className="flex flex-col gap-4">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={t("Search reporting year or framework ...")}
              className="pl-9"
            />
          </div>

          {/* <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("All Status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("All Status")}</SelectItem>
                <SelectItem value="active">{t("Active")}</SelectItem>
                <SelectItem value="closed">{t("Closed")}</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="icon" className="h-9 w-9">
              <Filter className="h-4 w-4" />
            </Button>
          </div> */}
        </div>

        {/* Table — NO pagination prop passed */}
        <DataTable
          columns={columns}
          data={paginatedData}
          loading={loading}
          emptyMessage={t("No reporting boundaries found")}
          sort={sort}
          onSort={handleSort}
          filtersEnabled={false}
          getRowKey={(row) => row.id || row._id}
        />

        {/* Pagination — handled here, outside DataTable */}
        {totalItems > 0 && (
          <PaginationControls
            page={currentPage}
            pageSize={pageSize}
            total={totalItems}
            onPageChange={setCurrentPage}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[PAGE_SIZE, 10, 30, 50]}
            labels={{
              itemsPerPage: t("Items per page"),
              previous: t("Previous"),
              next: t("Next"),
              pageLabel: ({ page, totalPages }) =>
                `${t("Page")} ${page} ${t("of")} ${totalPages}`,
            }}
            className="pt-2"
          />
        )}

        {/* <YearSetupProcess /> */}
        <ReportingBoundaryDetailDialog
          open={detailOpen}
          onOpenChange={(open) => {
            setDetailOpen(open);
            if (!open) {
              setDetailRow(null);
            }
          }}
          row={detailRow}
          organizationTree={organizationTree}
          availableFrameworks={availableFrameworks}
        />
        <ConfirmDialog
          open={!!deleteRow}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteRow(null);
            }
          }}
          title={t("Delete reporting year")}
          description={t(
            "Are you sure you want to delete this reporting year?",
          )}
          confirmLabel={t("Delete")}
          cancelLabel={t("Cancel")}
          loading={deleting}
          tone="danger"
          onConfirm={() => void handleDeleteConfirm()}
        />
      </div>
    );
  },
);

export default ReportingBoundaryTable;
